import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Unit } from '../types/index.ts';

interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  units: Unit[];
  selectedUnitTypes: string[];
  setSelectedUnitTypes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedStatuses: string[];
  setSelectedStatuses: React.Dispatch<React.SetStateAction<string[]>>;
  groupByType: boolean;
  setGroupByType: React.Dispatch<React.SetStateAction<boolean>>;
  groupByStatus: boolean;
  setGroupByStatus: React.Dispatch<React.SetStateAction<boolean>>;
  hideOOS: boolean;
}

const FilterDialog: React.FC<FilterDialogProps> = ({
  open,
  onClose,
  units,
  selectedUnitTypes,
  setSelectedUnitTypes,
  selectedStatuses,
  setSelectedStatuses,
  groupByType,
  setGroupByType,
  groupByStatus,
  setGroupByStatus,
  hideOOS
}) => {
  // Get unique unit types and statuses for filtering
  const getUniqueUnitTypes = () => {
    return Array.from(new Set(units.map((unit: Unit) => unit.type).filter(Boolean)));
  };

  const getUniqueStatuses = () => {
    // Always return all possible statuses, regardless of current unit statuses
    return ['I/S', 'E/R', 'O/S', 'TRX', 'OOS', 'DISP'];
  };

  const handleClearAll = () => {
    setSelectedUnitTypes([]);
    setSelectedStatuses([]);
    setGroupByType(false);
    setGroupByStatus(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ color: '#eceef1', backgroundColor: '#1a1d22', fontSize: 20, fontWeight: 'bold', padding: 3 }}>
        Filter Units
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#1a1d22', padding: 4 }}>
        {/* Unit Type Filters */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography sx={{ color: '#eceef1', fontSize: 18, fontWeight: 'bold', marginBottom: 2 }}>
            Unit Types:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {(getUniqueUnitTypes() as string[]).map((type: string) => (
              <Chip
                key={type}
                label={type}
                size="medium"
                clickable
                onClick={() => {
                  setSelectedUnitTypes((prev: string[]) => 
                    prev.includes(type) 
                      ? prev.filter((t: string) => t !== type)
                      : [...prev, type]
                  );
                }}
                sx={{
                  backgroundColor: selectedUnitTypes.includes(type) ? '#3c8cff' : '#464a52',
                  color: '#eceef1',
                  fontSize: 16,
                  fontWeight: 'bold',
                  height: 40,
                  padding: '0 12px',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: selectedUnitTypes.includes(type) ? '#2a5aa0' : '#5a5e66',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease'
                  },
                  '& .MuiChip-label': {
                    padding: '0 8px'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Status Filters */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography sx={{ color: '#eceef1', fontSize: 18, fontWeight: 'bold', marginBottom: 2 }}>
            Statuses:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {getUniqueStatuses().map((status) => {
              const isOOSDisabled = status === 'OOS' && hideOOS;
              return (
                <Chip
                  key={status}
                  label={status}
                  size="medium"
                  clickable={!isOOSDisabled}
                  onClick={() => {
                    if (!isOOSDisabled) {
                      setSelectedStatuses((prev: string[]) => 
                        prev.includes(status) 
                          ? prev.filter((s: string) => s !== status)
                          : [...prev, status]
                      );
                    }
                  }}
                  sx={{
                    backgroundColor: selectedStatuses.includes(status) ? '#3c8cff' : '#464a52',
                    color: isOOSDisabled ? '#666' : '#eceef1',
                    fontSize: 16,
                    fontWeight: 'bold',
                    height: 40,
                    padding: '0 12px',
                    borderRadius: 2,
                    opacity: isOOSDisabled ? 0.5 : 1,
                    cursor: isOOSDisabled ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      backgroundColor: isOOSDisabled ? '#464a52' : (selectedStatuses.includes(status) ? '#2a5aa0' : '#5a5e66'),
                      transform: isOOSDisabled ? 'none' : 'scale(1.05)',
                      transition: 'all 0.2s ease'
                    },
                    '& .MuiChip-label': {
                      padding: '0 8px'
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Grouping Options */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography sx={{ color: '#eceef1', fontSize: 18, fontWeight: 'bold' }}>
            Grouping Options:
          </Typography>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={groupByType}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupByType(e.target.checked)}
                  sx={{ 
                    color: '#464a52', 
                    '&.Mui-checked': { color: '#3c8cff' },
                    transform: 'scale(1.2)'
                  }}
                />
              }
              label={
                <Typography sx={{ color: '#eceef1', fontSize: 16, fontWeight: 'medium', marginLeft: 1 }}>
                  Group by Type
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={groupByStatus}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupByStatus(e.target.checked)}
                  sx={{ 
                    color: '#464a52', 
                    '&.Mui-checked': { color: '#3c8cff' },
                    transform: 'scale(1.2)'
                  }}
                />
              }
              label={
                <Typography sx={{ color: '#eceef1', fontSize: 16, fontWeight: 'medium', marginLeft: 1 }}>
                  Group by Status
                </Typography>
              }
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: '#1a1d22', padding: 3 }}>
        <Button 
          onClick={handleClearAll}
          sx={{ 
            color: '#b4b9c0',
            fontSize: 16,
            fontWeight: 'medium',
            padding: '8px 16px'
          }}
        >
          Clear All
        </Button>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            backgroundColor: '#3c8cff',
            fontSize: 16,
            fontWeight: 'bold',
            padding: '10px 24px',
            borderRadius: 2,
            '&:hover': {
              backgroundColor: '#2a5aa0',
              transform: 'scale(1.05)',
              transition: 'all 0.2s ease'
            }
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterDialog;